import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import {
  Repeat, CheckCircle, Loader2, ArrowLeft, Zap, Package,
  Clock, CreditCard, Info, Plus, Sparkles, History, AlertTriangle
} from 'lucide-react';
import IndividualBottomNav from '@/components/IndividualBottomNav';
import IndividualSidebar from '@/components/IndividualSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_planOptions = [
  { type: '10kg', kg: 10, price: 699,  months: 1, label: 'FoldPass 10kg', popular: false },
  { type: '20kg', kg: 20, price: 1199, months: 2, label: 'FoldPass 20kg', popular: true  },
  { type: '40kg', kg: 40, price: 2199, months: 3, label: 'FoldPass 40kg', popular: false },
];

const DEFAULT_ADDON_OPTIONS = [
  { kg: 2, price: 149, label: '+2 kg' },
  { kg: 5, price: 349, label: '+5 kg' },
];

// ── Clean Plan Card ───────────────────────────────────────────────────────────
const ActivePlanCard = ({ plan, isPending }) => {
  const total = plan.total_kg;
  const used  = plan.used_kg || 0;
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? (remaining / total) * 100 : 0;
  const daysLeft = plan.expiry_date
    ? Math.max(0, differenceInDays(new Date(plan.expiry_date), new Date()))
    : 0;

  const barColor = pct > 60 ? 'bg-emerald-500' : pct > 30 ? 'bg-orange-500' : 'bg-red-500';
  const daysColor = daysLeft <= 7 ? 'text-red-600 bg-red-50 border-red-100' : 'text-emerald-700 bg-emerald-50 border-emerald-100';

  // base kg = total minus all addon kg
  const addonTotal = (plan.addons || []).reduce((s, a) => s + (a.kg || 0), 0);
  const baseKg = total - addonTotal;

  return (
    <div className={`bg-white border rounded-[2rem] p-6 shadow-sm ${isPending ? 'border-amber-200' : 'border-slate-200/80'}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPending ? 'bg-amber-50' : 'bg-orange-50'}`}>
          <Repeat className={`w-5 h-5 ${isPending ? 'text-amber-500' : 'text-orange-500'}`} />
        </div>
        <div className="flex-1">
          <p className="text-base font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {plan.plan_name}
            {addonTotal > 0 && (
              <span className="ml-2 text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                +{addonTotal}kg add-on
              </span>
            )}
          </p>
          <p className="text-xs text-slate-400">Valid for {plan.validity_months} month{plan.validity_months > 1 ? 's' : ''}</p>
        </div>
        {isPending
          ? <span className="text-[10px] font-bold bg-amber-400 text-white px-2.5 py-1 rounded-full flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> Pending</span>
          : <span className="text-[10px] font-bold bg-emerald-500 text-white px-2.5 py-1 rounded-full flex items-center gap-1"><CheckCircle className="w-2.5 h-2.5" /> Active</span>
        }
      </div>

      {isPending ? (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
          Payment verification in progress. Your plan will activate automatically.
        </p>
      ) : (
        <>
          {/* KG bar */}
          <div className="mb-5">
            <div className="flex justify-between items-end mb-2">
              <div>
                <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {remaining.toFixed(1)} <span className="text-base font-semibold text-slate-400">kg left</span>
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{used.toFixed(1)} kg used of {total} kg total</p>
              </div>
              <span className="text-sm font-black" style={{ color: pct > 60 ? '#10B981' : pct > 30 ? '#F97316' : '#EF4444' }}>
                {Math.round(pct)}%
              </span>
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-3 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            {addonTotal > 0 && (
              <p className="text-[10px] text-blue-600 mt-1.5 font-medium">
                Base {baseKg}kg + {addonTotal}kg add-on = {total}kg total capacity
              </p>
            )}
          </div>

          {/* Low balance warning */}
          {remaining <= 4 && remaining > 0 && (
            <div className="mb-4 border border-orange-300 bg-orange-50 rounded-2xl px-4 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <p className="text-sm font-bold text-orange-700">Plan running low — only {remaining.toFixed(1)} kg left</p>
              </div>
              <ul className="text-xs text-orange-600 space-y-1 ml-6 list-disc">
                <li>Renew your plan — your remaining <strong>{remaining.toFixed(1)} kg will carry over</strong> to the new plan</li>
                <li>If you book a pickup now, <strong>delivery charges (₹6/km)</strong> will apply based on your address distance</li>
                <li>Pay the delivery charge when confirming your next pickup</li>
              </ul>
              <button
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                className="ml-6 text-xs font-bold text-orange-700 underline underline-offset-2 hover:text-orange-800"
              >
                Renew or Add KGs below ↓
              </button>
            </div>
          )}

          {/* Expiry + days left */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-2xl p-3 text-center border border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Expires</p>
              <p className="text-sm font-black text-slate-800">
                {format(new Date(plan.expiry_date), 'dd MMM yyyy')}
              </p>
            </div>
            <div className={`rounded-2xl p-3 text-center border ${daysColor}`}>
              <p className="text-[10px] uppercase tracking-widest mb-1" style={{ opacity: 0.7 }}>Days Left</p>
              <p className="text-sm font-black">{daysLeft} days</p>
            </div>
          </div>

          {/* Addons history (if any) */}
          {plan.addons?.length > 0 && (
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5">
              <p className="text-[10px] text-blue-700 font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add-On History
              </p>
              {plan.addons.map((a, i) => (
                <div key={i} className="flex justify-between text-xs text-blue-700 py-0.5">
                  <span>+{a.kg} kg added</span>
                  <span className="text-blue-500">{a.added_at ? format(new Date(a.added_at), 'dd MMM yyyy') : ''} · ₹{a.price}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-start gap-2 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2.5">
            <Info className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-orange-700">Please order more than 4kgs to avoid delivery charges. Delivery boy will weigh and log the KG at door.</p>
          </div>
        </>
      )}
    </div>
  );
};

// ── Add-On Section ────────────────────────────────────────────────────────────
const AddOnSection = ({ token, onSuccess, options = DEFAULT_ADDON_OPTIONS }) => {
  const [selected, setSelected] = useState(null);
  const [paying, setPaying] = useState(false);

  const handlePay = async () => {
    if (!selected) { toast.error('Select an add-on pack'); return; }
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/individual/plans/addon/initiate`,
        { addon_kg: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate payment');
      setPaying(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-[2rem] p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Plus className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <p className="font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Add More KG</p>
          <p className="text-xs text-slate-400">Top up your plan — same expiry date applies</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-4">
        {options.map(opt => (
          <button key={opt.kg} onClick={() => setSelected(opt.kg)}
            className={`p-4 rounded-2xl border-2 text-left transition-all ${
              selected === opt.kg
                ? 'bg-blue-600 border-blue-600 shadow-md shadow-blue-200'
                : 'bg-slate-50 border-slate-200 hover:border-blue-300'
            }`}>
            <p className={`text-lg font-black ${selected === opt.kg ? 'text-white' : 'text-slate-800'}`}
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              {opt.label}
            </p>
            <p className={`text-sm font-bold mt-0.5 ${selected === opt.kg ? 'text-blue-100' : 'text-blue-600'}`}>
              ₹{opt.price}
            </p>
            <p className={`text-[10px] mt-0.5 ${selected === opt.kg ? 'text-blue-200' : 'text-slate-400'}`}>
              ₹{(opt.price / opt.kg).toFixed(0)}/kg
            </p>
          </button>
        ))}
      </div>

      <button
        onClick={handlePay}
        disabled={!selected || paying}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-3.5 rounded-2xl transition-all text-sm shadow-md shadow-blue-200">
        {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
        {paying ? 'Redirecting...' : selected
          ? `Pay ₹${options.find(o => o.kg === selected)?.price} via Easebuzz`
          : 'Select a pack to continue'}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const IndividualFoldPass = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('individualToken');

  const [activePlan, setActivePlan] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [planHistory, setPlanHistory] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [selectedPlanType, setSelectedPlanType] = useState(null);
  const [initiating, setInitiating] = useState(false);
  const [planOptions, setPlanOptions] = useState(DEFAULT_planOptions);
  const [addonOptions, setAddonOptions] = useState(DEFAULT_ADDON_OPTIONS);

  useEffect(() => {
    // Fetch dynamic rates (public, no auth needed)
    axios.get(`${API}/individual/service-rates`)
      .then(r => {
        const data = r.data;
        if (data.foldpass_plan?.length) {
          const plans = data.foldpass_plan
            .sort((a, b) => (a.plan_kg || 0) - (b.plan_kg || 0))
            .map(r => ({
              type: `${r.plan_kg}kg`,
              kg: r.plan_kg,
              price: r.price,
              months: r.plan_months,
              label: r.label,
              popular: r.plan_popular || false,
            }));
          setPlanOptions(plans);
        }
        if (data.foldpass_addon?.length) {
          const addons = data.foldpass_addon
            .sort((a, b) => (a.addon_kg || 0) - (b.addon_kg || 0))
            .map(r => ({ kg: r.addon_kg, price: r.price, label: r.label }));
          setAddonOptions(addons);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) { navigate('/individual/login'); return; }
    fetchPlan();
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      if (params.get('addon')) {
        toast.success('Add-on activated! KG added to your FoldPass plan.');
      } else if (params.get('renewal')) {
        toast.success('Plan renewed! Your remaining KGs have been carried over.');
      } else {
        toast.success('Payment successful! Your FoldPass plan is now active.');
      }
      window.history.replaceState({}, '', '/individual/foldpass');
    } else if (params.get('payment') === 'failed') {
      if (params.get('renewal')) {
        toast.error('Renewal payment failed or cancelled. Please try again.');
      } else {
        toast.error('Payment failed or cancelled. Please try again.');
      }
      window.history.replaceState({}, '', '/individual/foldpass');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlan = async () => {
    setLoadingPlan(true);
    try {
      const [activeRes, histRes] = await Promise.all([
        axios.get(`${API}/individual/plans/active`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/individual/plans/history`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setActivePlan(activeRes.data.plan || null);
      setIsPending(activeRes.data.pending || false);
      setPlanHistory(histRes.data || []);
    } catch { /* ignore */ }
    finally { setLoadingPlan(false); }
  };

  const [renewPlanType, setRenewPlanType] = useState(null);
  const [renewing, setRenewing] = useState(false);

  const handleBuyPlan = async () => {
    if (!selectedPlanType) { toast.error('Select a plan first'); return; }
    setInitiating(true);
    try {
      const res = await axios.post(
        `${API}/payment/plan/initiate`,
        { plan_type: selectedPlanType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate payment');
      setInitiating(false);
    }
  };

  const handleRenewPlan = async () => {
    if (!renewPlanType) { toast.error('Select a plan to renew'); return; }
    setRenewing(true);
    try {
      const res = await axios.post(
        `${API}/individual/plans/renew/initiate`,
        { plan_type: renewPlanType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate renewal');
      setRenewing(false);
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/individual/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>FoldPass</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            <div className="hidden md:block">
              <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>FoldPass Plans</h1>
              <p className="text-sm text-slate-500 mt-0.5">Pre-pay for laundry and save. Order more than 4kgs to avoid delivery charges.</p>
            </div>

            {loadingPlan ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>

            ) : activePlan ? (
              <>
                {isPending && (
                  <div className="bg-amber-50 border border-amber-200 rounded-[1.5rem] p-4 flex items-start gap-3">
                    <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-amber-800 text-sm">Payment Verification Pending</p>
                      <p className="text-xs text-amber-700 mt-0.5">Your payment of <strong>₹{activePlan.price}</strong> is being verified. Plan activates automatically.</p>
                    </div>
                  </div>
                )}

                <ActivePlanCard plan={activePlan} isPending={isPending} />

                {/* Add-on section — only when plan is truly active */}
                {!isPending && <AddOnSection token={token} onSuccess={fetchPlan} options={addonOptions} />}

                {/* Renew Plan section */}
                {!isPending && (
                  <div className="bg-white border border-slate-200/80 rounded-[2rem] p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                        <Repeat className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-base font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Renew Your Plan</p>
                        <p className="text-xs text-slate-400">Remaining KGs carry over to your new plan</p>
                      </div>
                    </div>
                    {(() => {
                      const remaining = Math.max(0, activePlan.total_kg - (activePlan.used_kg || 0));
                      return remaining > 0 ? (
                        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 mb-4">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-emerald-700">
                            <strong>{remaining.toFixed(1)} kg</strong> from your current plan will carry over to the new plan at no extra cost.
                          </p>
                        </div>
                      ) : null;
                    })()}
                    <div className="space-y-2 mb-4">
                      {planOptions.map(plan => (
                        <button key={plan.type} onClick={() => setRenewPlanType(plan.type)}
                          className={`w-full p-4 rounded-2xl border-2 text-left transition-all relative ${
                            renewPlanType === plan.type
                              ? 'bg-blue-600 border-blue-600 shadow-lg shadow-blue-200'
                              : 'bg-slate-50 border-slate-200 hover:border-blue-300'
                          }`}>
                          {plan.popular && (
                            <span className="absolute -top-2 left-4 text-[9px] font-black bg-orange-500 text-white px-2 py-0.5 rounded-full">Most Popular</span>
                          )}
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-sm font-black ${renewPlanType === plan.type ? 'text-white' : 'text-slate-800'}`}
                                style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.label}</p>
                              <p className={`text-xs mt-0.5 ${renewPlanType === plan.type ? 'text-blue-100' : 'text-slate-400'}`}>
                                {plan.kg}kg base · {plan.months} month{plan.months > 1 ? 's' : ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-black ${renewPlanType === plan.type ? 'text-white' : 'text-blue-600'}`}>₹{plan.price}</p>
                              <p className={`text-[10px] ${renewPlanType === plan.type ? 'text-blue-100' : 'text-slate-400'}`}>₹{(plan.price / plan.kg).toFixed(0)}/kg</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={handleRenewPlan}
                      disabled={!renewPlanType || renewing}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-3.5 rounded-2xl shadow-md shadow-blue-200 transition-all text-sm">
                      {renewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      {renewing ? 'Redirecting...' : renewPlanType
                        ? `Renew for ₹${planOptions.find(p => p.type === renewPlanType)?.price} via Easebuzz`
                        : 'Select a plan to renew'}
                    </button>
                  </div>
                )}

                {/* Plan history */}
                {planHistory.filter(p => p.status !== 'pending_payment').length > 1 && (
                  <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
                    <p className="text-sm font-black text-slate-700 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <History className="w-4 h-4 text-slate-400" /> Plan History
                    </p>
                    <div className="space-y-2">
                      {planHistory.filter(p => p.status !== 'pending_payment').map(p => (
                        <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                          <div>
                            <p className="text-sm font-semibold text-slate-700">{p.plan_name}</p>
                            <p className="text-xs text-slate-400">{format(new Date(p.start_date), 'dd MMM yyyy')}</p>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            p.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                            p.status === 'exhausted' ? 'bg-orange-50 text-orange-700' :
                            p.status === 'renewed' ? 'bg-blue-50 text-blue-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>{
                            p.status === 'active' ? 'Active' :
                            p.status === 'exhausted' ? 'Exhausted' :
                            p.status === 'renewed' ? 'Renewed' : 'Expired'
                          }{p.carry_over_kg > 0 ? ` (+${p.carry_over_kg}kg carry)` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>

            ) : (
              <>
                {/* Hero banner */}
                <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700 rounded-[2rem] p-6 text-white relative overflow-hidden shadow-lg shadow-blue-200">
                  <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                      <Repeat className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Get FoldPass</h2>
                    <p className="text-blue-100 text-sm mb-4">Pre-pay for laundry, save more per kg. Order more than 4kgs to avoid delivery charges.</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { icon: Zap,      label: 'Priority pickup' },
                        { icon: Package,  label: 'Order 4kg+ free delivery' },
                        { icon: Sparkles, label: 'Up to 3 months' },
                      ].map(({ icon: Icon, label }) => (
                        <div key={label} className="bg-white/15 rounded-xl px-2 py-2 text-center">
                          <Icon className="w-4 h-4 text-white mx-auto mb-1" />
                          <p className="text-[10px] text-white/80 font-medium">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Plan cards */}
                <div className="space-y-3">
                  {planOptions.map(plan => (
                    <button key={plan.type} onClick={() => setSelectedPlanType(plan.type)}
                      className={`w-full p-5 rounded-[1.5rem] border-2 text-left transition-all relative ${
                        selectedPlanType === plan.type
                          ? 'bg-blue-600 border-blue-600 shadow-lg shadow-blue-200'
                          : 'bg-white border-slate-200 hover:border-blue-300'
                      }`}>
                      {plan.popular && (
                        <span className="absolute -top-2.5 left-5 text-[10px] font-black bg-orange-500 text-white px-2.5 py-0.5 rounded-full">
                          Most Popular
                        </span>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-base font-black ${selectedPlanType === plan.type ? 'text-white' : 'text-slate-800'}`}
                            style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.label}</p>
                          <p className={`text-xs mt-0.5 ${selectedPlanType === plan.type ? 'text-blue-100' : 'text-slate-400'}`}>
                            {plan.kg}kg · valid {plan.months} month{plan.months > 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-black ${selectedPlanType === plan.type ? 'text-white' : 'text-blue-600'}`}>₹{plan.price}</p>
                          <p className={`text-[10px] ${selectedPlanType === plan.type ? 'text-blue-100' : 'text-slate-400'}`}>
                            ₹{(plan.price / plan.kg).toFixed(0)}/kg
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleBuyPlan}
                  disabled={!selectedPlanType || initiating}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-4 rounded-2xl shadow-lg shadow-orange-200 transition-all hover:-translate-y-0.5 text-base">
                  {initiating ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                  {initiating ? 'Redirecting to payment...' : selectedPlanType
                    ? `Pay ₹${planOptions.find(p => p.type === selectedPlanType)?.price} via Easebuzz`
                    : 'Select a plan to continue'}
                </button>

                <p className="text-center text-xs text-slate-400">
                  Secured by Easebuzz payment gateway · Plan activates instantly after payment
                </p>
              </>
            )}
          </div>
        </main>
      </div>

      <IndividualBottomNav />
    </div>
  );
};

export default IndividualFoldPass;
